import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useSEO, generateServiceSchema, generateBreadcrumbSchema } from '../../../utils/seo';

export default function LongDistanceTransport() {
  const serviceSchema = generateServiceSchema(
    'Long Distance Medical Transportation',
    'Reliable long-distance medical transportation services across California and neighboring states. Comfortable, safe transport for medical appointments, hospital transfers, and relocations with professional care.'
  );
  
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/#services' },
    { name: 'Long Distance Transport', url: '/services/long-distance' }
  ]);

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [serviceSchema, breadcrumbSchema]
  };

  useSEO({
    title: 'Long Distance Medical Transportation | Interstate NEMT Services California',
    description: 'Professional long-distance medical transportation across California and neighboring states. Safe, comfortable interstate medical transport for appointments, transfers, and relocations.',
    keywords: 'long distance medical transport, interstate medical transportation, cross country medical transport, long distance NEMT, California medical transport',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/long-distance`,
    schema: combinedSchema
  });

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/0b56c2d5800c5f8a499a90eb837d18df.jpg')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Long-Distance Medical Transportation
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              Comfortable, safe medical transportation for appointments and treatments outside your local area, anywhere in the country
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => window.location.href = '/booking'}
                className="text-lg px-8 py-4"
              >
                <i className="ri-calendar-line mr-2"></i>
                Book Long-Distance Transport
              </Button>
              <Button 
                variant="secondary" 
                size="lg"
                onClick={() => window.location.href = 'tel:+17147987318'}
                className="text-lg px-8 py-4"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Long-Distance Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Interstate & Cross-Country Medical Transport
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                When you need specialized medical care that's not available locally, InteCare's long-distance medical transportation service gets you there safely and comfortably. We provide medical transport across state lines and throughout the country.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our long-distance vehicles are specially equipped for extended travel with enhanced comfort features, medical equipment, and trained staff to ensure your well-being throughout the journey.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Nationwide coverage and interstate transport</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Extended comfort features for long journeys</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Multiple driver teams for continuous service</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Meal stops and comfort breaks included</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Interior%20view%20of%20luxury%20long-distance%20medical%20transport%20vehicle%20showing%20comfortable%20reclining%20seats%2C%20entertainment%20systems%2C%20climate%20control%2C%20medical%20equipment%2C%20spacious%20patient%20area%2C%20long%20journey%20comfort%20features%2C%20professional%20medical%20transport%20interior&width=600&height=400&seq=longdistance-interior&orientation=landscape"
                alt="Long-Distance Transport Interior"
                className="w-full rounded-lg shadow-lg object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* When You Need Long-Distance Transport */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              When Do You Need Long-Distance Medical Transport?
            </h2>
            <p className="text-xl text-gray-600">
              Specialized care often requires travel to distant medical centers
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hospital-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Specialized Treatment Centers</h3>
              <p className="text-gray-600">
                Transport to renowned medical centers, cancer treatment facilities, or specialized hospitals.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-surgical-mask-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Surgical Procedures</h3>
              <p className="text-gray-600">
                Travel to hospitals with specialized surgical teams or cutting-edge procedures not available locally.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-medicine-bottle-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Clinical Trials</h3>
              <p className="text-gray-600">
                Participation in medical research studies and clinical trials at research institutions.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-brain-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Neurological Care</h3>
              <p className="text-gray-600">
                Treatment at specialized neurological centers for brain, spine, and nervous system conditions.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-pulse-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cardiac Specialists</h3>
              <p className="text-gray-600">
                Access to leading cardiologists and heart surgery centers for complex cardiac conditions.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-heart-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Family Relocation</h3>
              <p className="text-gray-600">
                Moving closer to family or relocating to areas with better healthcare access.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Transport Options */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Long-Distance Transport Options
            </h2>
            <p className="text-xl text-gray-600">
              Choose the service level that best meets your medical and comfort needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-gradient-to-b from-blue-50 to-white rounded-xl p-8 shadow-lg border">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-wheelchair-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Standard Transport</h3>
                <p className="text-gray-600 mt-2">Ambulatory and wheelchair accessible</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Comfortable seating</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Basic medical monitoring</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Meal and rest stops</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Professional driver</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-b from-green-50 to-white rounded-xl p-8 shadow-lg border-2 border-green-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-pulse-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Medical Support</h3>
                <p className="text-gray-600 mt-2">With EMT or nursing staff</p>
                <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm mt-2 inline-block">
                  Most Popular
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Everything in Standard</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">EMT or nurse accompaniment</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Continuous monitoring</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Medication management</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-b from-purple-50 to-white rounded-xl p-8 shadow-lg border">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-hospital-line text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Critical Care</h3>
                <p className="text-gray-600 mt-2">Advanced life support equipped</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Everything in Medical Support</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Paramedic team</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">Advanced life support</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  <span className="text-gray-700">ICU-level monitoring</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planning Your Journey */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Planning Your Long-Distance Journey
            </h2>
            <p className="text-xl text-gray-600">
              We handle all the details so you can focus on your health
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Initial Consultation</h3>
              <p className="text-gray-600">
                Discuss your medical needs, destination, timeline, and special requirements.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Route Planning</h3>
              <p className="text-gray-600">
                We plan the safest, most comfortable route with appropriate stops and accommodations.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Medical Coordination</h3>
              <p className="text-gray-600">
                Coordinate with your current and receiving medical teams for seamless care transition.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Safe Transport</h3>
              <p className="text-gray-600">
                Professional, comfortable journey with continuous care and communication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Nationwide Coverage
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              InteCare provides long-distance medical transportation throughout the United States. No distance is too far when your health is on the line.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="bg-blue-50 rounded-lg p-6">
                <i className="ri-map-2-line text-3xl text-blue-600 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">All 50 States</h3>
                <p className="text-gray-600">Complete coverage across the entire United States</p>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6">
                <i className="ri-road-map-line text-3xl text-green-600 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Interstate Transport</h3>
                <p className="text-gray-600">Licensed for interstate medical transportation</p>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-6">
                <i className="ri-time-line text-3xl text-purple-600 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">24/7 Service</h3>
                <p className="text-gray-600">Round-the-clock availability for urgent transports</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need Long-Distance Medical Transport?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Get the specialized care you need, no matter how far. InteCare's long-distance medical transportation connects you to the best healthcare anywhere in the country.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => window.location.href = '/booking'}
              className="bg-red-600 hover:bg-red-700 text-lg px-8 py-4"
            >
              <i className="ri-calendar-line mr-2"></i>
              Plan Your Journey
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => window.location.href = 'tel:+17147987318'}
              className="border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8 py-4"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (555) INTECARE
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}